import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import getMessages from '../../lib/i18n/getMessages';
import BuyerWorkflowControls from '../../components/Succession/BuyerRoom/BuyerWorkflowControls';
import { 
  SUBMIT_PROPOSAL, 
  BUYER_DECLINE_INVITATION, 
  SIGN_RECEIVING_PARTY_NDA,
  SCHEDULE_DEAL_MEETING 
} from '../../graphql/mutations/dealMutations';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { CurrencyProvider } from '../../hooks/useCurrency';

// Success Animation Component
const SuccessAnimation = ({ message, onComplete }) => {
  const [particles, setParticles] = useState([]);
  
  React.useEffect(() => {
    // Create more particles and make them bigger
    const newParticles = Array.from({ length: 50 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: 0,
      rotation: Math.random() * 360,
      scale: Math.random() * 0.8 + 0.8, // Increased base size
      color: ['#3B82F6', '#10B981', '#6366F1', '#8B5CF6', '#EC4899'][Math.floor(Math.random() * 5)]
    }));
    
    setParticles(newParticles);
    
    // Cleanup after animation
    const timer = setTimeout(() => {
      onComplete();
    }, 2500); // Slightly longer duration
    
    return () => clearTimeout(timer);
  }, [onComplete]);
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {/* Success Message - Made bigger */}
      <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-white rounded-xl shadow-2xl p-6 animate-slide-down min-w-[300px]">
        <div className="flex items-center space-x-4">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircleIcon className="h-8 w-8 text-green-500" />
          </div>
          <span className="text-xl font-semibold text-gray-800">{message}</span>
        </div>
      </div>
      
      {/* Particles - Made bigger */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute w-4 h-4 rounded-full" // Increased size
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            backgroundColor: particle.color,
            transform: `rotate(${particle.rotation}deg) scale(${particle.scale})`,
            animation: `particle-fall 1.5s ease-out forwards`, // Slightly longer duration
            animationDelay: `${Math.random() * 0.5}s`,
            boxShadow: '0 0 10px rgba(255,255,255,0.5)' // Added glow effect
          }}
        />
      ))}
    </div>
  );
};

// GraphQL query to get deal interest data for buyer workflow
const GET_BUYER_WORKFLOW_DATA = gql`
  query GetBuyerWorkflowData($dealId: String!) {
    getDeal(name: $dealId) {
      id
      deal_name
      headline
      sub_headline
      list_price
      invitation_deadline
      deal_state
      question_1
      question_2
      question_3
      question_4
      question_5
    }
    getUserDealInterestForDeal(dealName: $dealId) {
      id
      workflow_state
      invited
      saved
      shortlisted
      proposal_letter
      proposal_meeting_availability
      receiving_party_nda_sent_timestamp
      disclosing_party_nda_sent_timestamp
      buyer_declined_date
      a1
      a2
      a3
      a4
      a5
      deal {
        id
        deal_name
      }
    }
    company {
      id
      name
      deal_room_branded_name
      deal_room_branded_name_fr
    }
    getCurrentUser {
      id
      name
      allowed_name
      profile_picture
      features
    }
  }
`;

const BuyerWorkflowControlsContainer = ({ dealId }, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = railsContext && railsContext.currency;
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Use useQuery hook
  const { loading, error, data, refetch } = useQuery(GET_BUYER_WORKFLOW_DATA, {
    variables: { dealId },
    errorPolicy: 'all'
  });

  // Use useMutation hooks
  const [submitProposal, { loading: submitLoading }] = useMutation(SUBMIT_PROPOSAL);
  const [buyerDeclineInvitation, { loading: declineLoading }] = useMutation(BUYER_DECLINE_INVITATION);
  const [signReceivingPartyNda, { loading: ndaLoading }] = useMutation(SIGN_RECEIVING_PARTY_NDA);
  const [scheduleDealMeeting, { loading: meetingLoading }] = useMutation(SCHEDULE_DEAL_MEETING);

  // Handle loading state
  if (loading) return <div className="animate-pulse bg-gray-200 h-64 rounded-lg"></div>;
  
  // Handle error state
  if (error) {
    console.error('Error loading buyer workflow data:', error);
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700 text-sm">
          Error loading workflow information. Please refresh the page.
        </p>
      </div>
    );
  }

  const { getDeal: deal, getUserDealInterestForDeal: dealInterest, company, getCurrentUser: currentUser } = data || {};
  const hasUserDemoSecretMenu = currentUser?.features?.includes('demo_secret_menu');

  if (!deal || !dealInterest) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-700 text-sm">
          Deal or deal interest not found.
        </p>
      </div>
    );
  }

  const handleSubmitProposal = (input) => {
    console.log('handleSubmitProposal called with input:', input);
    
    // Transform snake_case to camelCase for GraphQL input
    const graphqlInput = {
      dealInterestId: input.deal_interest_id,
      proposalLetter: input.proposal_letter,
      proposalMeetingAvailability: input.proposal_meeting_availability,
      a1: input.a1,
      a2: input.a2,
      a3: input.a3,
      a4: input.a4,
      a5: input.a5
    };
    
    submitProposal({
      variables: { input: graphqlInput }
    })
    .then(({ data }) => {
      if (data.submitProposal.errors && data.submitProposal.errors.length > 0) {
        console.error('Error submitting proposal:', data.submitProposal.errors);
        alert(`Error: ${data.submitProposal.errors.join(', ')}`);
      } else {
        console.log('Proposal submitted successfully:', data.submitProposal.dealInterest);
        // Refresh the data to show updated state
        refetch();
        // Show success message
        // Trigger confetti success animation
        const locale = window.locale || 'en';
        const t = getMessages(locale);
        setSuccessMessage(t['succession.buyer_room.workflow_controls.eoi_submitted_success'] || getMessages('en')['succession.buyer_room.workflow_controls.eoi_submitted_success']);
        setShowSuccess(true);
      }
    })
    .catch((error) => {
      console.error('GraphQL error submitting proposal:', error);
      alert('An error occurred while submitting your proposal. Please try again.');
    });
  };

  const handleDeclineInvitation = () => {
    if (window.confirm('Are you sure you want to decline this invitation? This action cannot be undone.')) {
      buyerDeclineInvitation({
        variables: {
          dealId: deal.id,
          dealInterestId: dealInterest.id
        }
      })
      .then(({ data }) => {
        if (data.buyerDeclineInvitation.errors && data.buyerDeclineInvitation.errors.length > 0) {
          console.error('Error declining invitation:', data.buyerDeclineInvitation.errors);
          alert(`Error: ${data.buyerDeclineInvitation.errors.join(', ')}`);
        } else {
          // Refresh the data to show updated state
          refetch();
          // Optionally navigate away or show success message
          window.location.href = `/succession/deals`;
        }
      })
      .catch((error) => {
        console.error('GraphQL error declining invitation:', error);
        alert('An error occurred while declining the invitation. Please try again.');
      });
    }
  };

  const handleSignNDA = () => {
    // Navigate to NDA signing page - DocuSign will redirect to completion action
    // with the return URL, then completion action will redirect back to buyer room
    // it needs to have the protocol and host name and potentially the port if it't in the current location
    const returnUrl = `${window.location.protocol}//${window.location.host}/succession/deals/${deal.deal_name}/buyer_signed_nda?dealInterestId=${dealInterest.id}&dealId=${deal.id}`;
    window.location.href = `/deal/${deal.deal_name}/nda_sign_receiver?return_url=${returnUrl}`;
  };

  const handleViewDocuments = () => {
    // Open documents in new tab
    window.open(`/succession/deals/${deal.deal_name}/documents/${dealInterest.id}`, '_blank');
  };

  const handleScheduleMeeting = () => {
    // Navigate to meeting scheduling page
    window.location.href = `/succession/deals/${deal.deal_name}/schedule-meeting/${dealInterest.id}`;
  };

  const handleStatusUpdate = () => {
    // Refresh data when status updates
    refetch();
  };

  const locale = window.locale;

  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <>
          {/* Success Animation */}
          {showSuccess && (
            <SuccessAnimation
              message={successMessage}
              onComplete={() => setShowSuccess(false)}
            />
          )}

          <BuyerWorkflowControls
        deal={deal}
        dealInterest={dealInterest}
        company={company}
        currentUser={currentUser}
        loading={submitLoading || declineLoading || ndaLoading || meetingLoading}
        onSubmitProposal={handleSubmitProposal}
        onDeclineInvitation={handleDeclineInvitation}
        onSignNDA={handleSignNDA}
        onViewDocuments={handleViewDocuments}
        onScheduleMeeting={handleScheduleMeeting}
        onStatusUpdate={handleStatusUpdate}
        demoSecretMenuEnabled={hasUserDemoSecretMenu}
      />
      
        {/* CSS Styles for animations */}
        <style jsx>{`
          @keyframes particle-fall {
            0% {
              transform: translateY(0) rotate(0deg) scale(1);
              opacity: 1;
            }
            100% {
              transform: translateY(100vh) rotate(720deg) scale(0); // More rotation
              opacity: 0;
            }
          }

          @keyframes slide-down {
            0% {
              transform: translate(-50%, -100%);
              opacity: 0;
            }
            10% {
              transform: translate(-50%, 0);
              opacity: 1;
            }
            90% {
              transform: translate(-50%, 0);
              opacity: 1;
            }
            100% {
              transform: translate(-50%, -100%);
              opacity: 0;
            }
          }

          .animate-slide-down {
            animation: slide-down 2.5s ease-in-out forwards;
          }
        `}</style>
        </>
      </IntlProvider>
    </CurrencyProvider>
  );
};

BuyerWorkflowControlsContainer.propTypes = {
  dealId: PropTypes.string.isRequired
};

export default BuyerWorkflowControlsContainer; 